/**
 * Stat tile config (S6) — value column, discriminated format, compare mode,
 * explicit sparkline columns (never inferred — S4 contract) and severity
 * thresholds (drives S4's `thresholdEval` tinting).
 */
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import type { TileThreshold } from '../../types/dashboardTypes';
import { FieldRow, ColumnSelect, EnumSelect, type SectionProps } from './configFields';

export function StatConfigSection({ values, set, columnInfos }: SectionProps) {
  const setThresholds = (next: TileThreshold[]) => set({ statThresholds: next });
  return (
    <div className="flex flex-col gap-2" data-testid="add-tile-config-stat">
      <FieldRow label="Value column">
        <ColumnSelect
          value={values.statValueColumn}
          onChange={(statValueColumn) => set({ statValueColumn })}
          columnInfos={columnInfos}
          categories={['numeric']}
          placeholder="First numeric column"
          allowNone
          testId="add-tile-stat-value"
        />
      </FieldRow>
      <FieldRow label="Format">
        <div className="flex items-center gap-1.5">
          <EnumSelect
            value={values.statFormatKind}
            onChange={(statFormatKind) => set({ statFormatKind })}
            options={[
              { value: 'number', label: 'Number' },
              { value: 'percent', label: 'Percent' },
              { value: 'currency', label: 'Currency' },
              { value: 'duration', label: 'Duration' },
            ]}
            testId="add-tile-stat-format"
          />
          {values.statFormatKind === 'percent' && (
            <EnumSelect
              value={values.statPercentScale}
              onChange={(statPercentScale) => set({ statPercentScale })}
              options={[
                { value: 'fraction', label: '0.42 → 42%' },
                { value: 'percent', label: '42 → 42%' },
              ]}
            />
          )}
          {values.statFormatKind === 'currency' && (
            <Input
              className="h-7 w-20 text-xs uppercase"
              value={values.statCurrencyCode}
              maxLength={3}
              onChange={(e) => set({ statCurrencyCode: e.target.value.toUpperCase() })}
              aria-label="Currency code"
            />
          )}
          {values.statFormatKind === 'duration' && (
            <EnumSelect
              value={values.statDurationUnit}
              onChange={(statDurationUnit) => set({ statDurationUnit })}
              options={[
                { value: 'ms', label: 'milliseconds' },
                { value: 's', label: 'seconds' },
                { value: 'min', label: 'minutes' },
              ]}
            />
          )}
        </div>
      </FieldRow>
      <FieldRow label="Compare">
        <div className="flex items-center gap-1.5">
          <EnumSelect
            value={values.statCompareKind}
            onChange={(statCompareKind) => set({ statCompareKind })}
            options={[
              { value: 'none', label: 'None' },
              { value: 'previous-period', label: 'Previous value column' },
              { value: 'target', label: 'Target value' },
            ]}
          />
          {values.statCompareKind === 'previous-period' && (
            <ColumnSelect
              value={values.statCompareColumn}
              onChange={(statCompareColumn) => set({ statCompareColumn })}
              columnInfos={columnInfos}
              categories={['numeric']}
            />
          )}
          {values.statCompareKind === 'target' && (
            <Input
              type="number"
              className="h-7 w-28 text-xs"
              value={values.statCompareTarget}
              onChange={(e) => set({ statCompareTarget: e.target.valueAsNumber || 0 })}
              aria-label="Target value"
            />
          )}
        </div>
      </FieldRow>
      <FieldRow label="Sparkline">
        <div className="flex items-center gap-1.5">
          <Switch
            checked={values.statSparkline}
            onCheckedChange={(statSparkline) => set({ statSparkline })}
            data-testid="add-tile-stat-sparkline"
          />
          {values.statSparkline && (
            <>
              <ColumnSelect
                value={values.statSparkTime}
                onChange={(statSparkTime) => set({ statSparkTime })}
                columnInfos={columnInfos}
                categories={['date']}
                placeholder="Time column"
              />
              <ColumnSelect
                value={values.statSparkValue}
                onChange={(statSparkValue) => set({ statSparkValue })}
                columnInfos={columnInfos}
                categories={['numeric']}
                placeholder="Value column"
              />
            </>
          )}
        </div>
      </FieldRow>
      <div className="grid grid-cols-[92px_1fr] items-start gap-2">
        <Label className="pt-1.5 text-xs text-muted-foreground">Thresholds</Label>
        <div className="flex flex-col gap-1.5" data-testid="add-tile-stat-thresholds">
          {values.statThresholds.map((rule, index) => (
            <div key={index} className="flex items-center gap-1.5">
              <EnumSelect
                value={rule.operator}
                onChange={(operator) =>
                  setThresholds(
                    values.statThresholds.map((r, i) => (i === index ? { ...r, operator } : r)),
                  )
                }
                options={(['>', '>=', '<', '<='] as const).map((op) => ({ value: op, label: op }))}
              />
              <Input
                type="number"
                className="h-7 w-24 text-xs"
                value={rule.value}
                onChange={(e) =>
                  setThresholds(
                    values.statThresholds.map((r, i) =>
                      i === index ? { ...r, value: e.target.valueAsNumber || 0 } : r,
                    ),
                  )
                }
                aria-label="Threshold value"
              />
              <EnumSelect
                value={rule.severity}
                onChange={(severity) =>
                  setThresholds(
                    values.statThresholds.map((r, i) => (i === index ? { ...r, severity } : r)),
                  )
                }
                options={[
                  { value: 'warning', label: 'Warning' },
                  { value: 'critical', label: 'Critical' },
                ]}
              />
              <button
                type="button"
                className="rounded-sm p-1 text-muted-foreground hover:text-destructive"
                onClick={() => setThresholds(values.statThresholds.filter((_, i) => i !== index))}
                aria-label="Remove threshold"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 w-fit px-1.5 text-xs"
            onClick={() =>
              setThresholds([...values.statThresholds, { operator: '>=', value: 0, severity: 'warning' }])
            }
            data-testid="add-tile-add-threshold"
          >
            <Plus className="mr-1 h-3 w-3" /> Add threshold
          </Button>
        </div>
      </div>
    </div>
  );
}
